import React, { useRef, useState, useEffect, useCallback } from 'react';

const ZONES = [
  { id: 'rect', label: 'Red Rectangle', x: 30, y: 30, w: 160, h: 90, color: '#e53935', shape: 'rect' },
  { id: 'circle', label: 'Blue Circle', cx: 320, cy: 80, r: 55, color: '#1e88e5', shape: 'circle' },
  { id: 'tri', label: 'Green Triangle', points: [[500, 150], [430, 30], [570, 30]], color: '#43a047', shape: 'triangle' },
];

const W = 640;
const H = 200;
const FILL_THRESHOLD = 0.45;

function getZonePixelCount(zone) {
  if (zone.shape === 'rect') return zone.w * zone.h;
  if (zone.shape === 'circle') return Math.PI * zone.r * zone.r;
  const [[ax, ay], [bx, by], [cx, cy]] = zone.points;
  return Math.abs((bx - ax) * (cy - ay) - (cx - ax) * (by - ay)) / 2;
}

function isInZone(zone, px, py) {
  if (zone.shape === 'rect') {
    return px >= zone.x && px <= zone.x + zone.w && py >= zone.y && py <= zone.y + zone.h;
  }
  if (zone.shape === 'circle') {
    return Math.hypot(px - zone.cx, py - zone.cy) <= zone.r;
  }
  if (zone.shape === 'triangle') {
    const [[ax, ay], [bx, by], [cx, cy]] = zone.points;
    const dX = px - cx; const dY = py - cy;
    const dX21 = cx - bx; const dY12 = by - cy;
    const D = dY12 * (ax - cx) + dX21 * (ay - cy);
    const s = dY12 * dX + dX21 * dY;
    const t = (cy - ay) * dX + (ax - cx) * dY;
    if (D < 0) return s <= 0 && t <= 0 && s + t >= D;
    return s >= 0 && t >= 0 && s + t <= D;
  }
  return false;
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}

function colorDistance(r1, g1, b1, r2, g2, b2) {
  return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
}

export default function C21_CanvasPaint({ onComplete }) {
  const canvasRef = useRef(null);
  const [painting, setPainting] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#e53935');
  const [brushSize, setBrushSize] = useState(18);
  const [evaluated, setEvaluated] = useState(false);
  const [zoneResults, setZoneResults] = useState(null);
  const lastPos = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);

    ZONES.forEach(zone => {
      ctx.save();
      ctx.setLineDash([6, 4]);
      ctx.strokeStyle = zone.color;
      ctx.lineWidth = 2;
      ctx.globalAlpha = 0.5;
      if (zone.shape === 'rect') {
        ctx.strokeRect(zone.x, zone.y, zone.w, zone.h);
        ctx.font = '11px sans-serif';
        ctx.fillStyle = zone.color;
        ctx.globalAlpha = 0.7;
        ctx.fillText(zone.label, zone.x + 4, zone.y + 14);
      } else if (zone.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(zone.cx, zone.cy, zone.r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.font = '11px sans-serif';
        ctx.fillStyle = zone.color;
        ctx.globalAlpha = 0.7;
        ctx.textAlign = 'center';
        ctx.fillText(zone.label, zone.cx, zone.cy + 4);
      } else if (zone.shape === 'triangle') {
        ctx.beginPath();
        ctx.moveTo(zone.points[0][0], zone.points[0][1]);
        ctx.lineTo(zone.points[1][0], zone.points[1][1]);
        ctx.lineTo(zone.points[2][0], zone.points[2][1]);
        ctx.closePath();
        ctx.stroke();
        const cx = (zone.points[0][0] + zone.points[1][0] + zone.points[2][0]) / 3;
        const cy = (zone.points[0][1] + zone.points[1][1] + zone.points[2][1]) / 3;
        ctx.font = '11px sans-serif';
        ctx.fillStyle = zone.color;
        ctx.globalAlpha = 0.7;
        ctx.textAlign = 'center';
        ctx.fillText(zone.label, cx, cy + 4);
      }
      ctx.restore();
    });
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = W / rect.width;
    const scaleY = H / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const paint = useCallback((e) => {
    if (!painting || evaluated) return;
    e.preventDefault();
    const pos = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.globalAlpha = 1;
    ctx.strokeStyle = selectedColor;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    if (lastPos.current) {
      ctx.moveTo(lastPos.current.x, lastPos.current.y);
    } else {
      ctx.moveTo(pos.x, pos.y);
    }
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  }, [painting, selectedColor, brushSize, evaluated]);

  const startPaint = useCallback((e) => {
    if (evaluated) return;
    setPainting(true);
    lastPos.current = null;
    paint(e);
  }, [evaluated, paint]);

  const stopPaint = useCallback(() => {
    setPainting(false);
    lastPos.current = null;
  }, []);

  const handleClear = () => {
    if (evaluated) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    ZONES.forEach(zone => {
      ctx.save();
      ctx.setLineDash([6, 4]);
      ctx.strokeStyle = zone.color;
      ctx.lineWidth = 2;
      ctx.globalAlpha = 0.5;
      if (zone.shape === 'rect') {
        ctx.strokeRect(zone.x, zone.y, zone.w, zone.h);
      } else if (zone.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(zone.cx, zone.cy, zone.r, 0, Math.PI * 2);
        ctx.stroke();
      } else if (zone.shape === 'triangle') {
        ctx.beginPath();
        ctx.moveTo(zone.points[0][0], zone.points[0][1]);
        ctx.lineTo(zone.points[1][0], zone.points[1][1]);
        ctx.lineTo(zone.points[2][0], zone.points[2][1]);
        ctx.closePath();
        ctx.stroke();
      }
      ctx.restore();
    });
  };

  const handleEvaluate = () => {
    setEvaluated(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, W, H);
    const pixels = imageData.data;

    const results = ZONES.map(zone => {
      const [tr, tg, tb] = hexToRgb(zone.color);
      let zoneTotal = 0;
      let zoneCorrect = 0;
      for (let py = 0; py < H; py++) {
        for (let px = 0; px < W; px++) {
          if (!isInZone(zone, px, py)) continue;
          zoneTotal++;
          const idx = (py * W + px) * 4;
          const r = pixels[idx], g = pixels[idx + 1], b = pixels[idx + 2];
          if (colorDistance(r, g, b, tr, tg, tb) < 80) zoneCorrect++;
        }
      }
      const fill = zoneTotal > 0 ? zoneCorrect / zoneTotal : 0;
      const pass = fill >= FILL_THRESHOLD;
      return { zone, fill, pass, zoneTotal, zoneCorrect };
    });

    setZoneResults(results);
    const passed = results.filter(r => r.pass).length;
    const score = Math.round((passed / ZONES.length) * 100);
    const details = {};
    results.forEach(r => {
      details[r.zone.id] = {
        label: r.zone.label,
        fillPct: Math.round(r.fill * 100),
        pass: r.pass,
        expected: `≥${Math.round(FILL_THRESHOLD * 100)}% filled with ${r.zone.color}`,
      };
    });
    onComplete({ score, details, passed: score === 100 });
  };

  const COLORS = ZONES.map(z => z.color).concat(['#000000', '#ffffff']);

  return (
    <div className="ab-challenge-ui">
      <p className="ab-hint">
        Fill each outlined zone with its matching color using the brush. Red zone → red, Blue zone → blue, Green zone → green.
        Fill at least 45% of each zone to pass.
      </p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, opacity: 0.7 }}>Color:</span>
          {COLORS.map(c => (
            <button
              key={c}
              onClick={() => setSelectedColor(c)}
              style={{
                width: 26, height: 26, borderRadius: '50%', background: c,
                border: selectedColor === c ? '3px solid var(--text)' : '2px solid var(--border2)',
                cursor: 'pointer', outline: selectedColor === c ? '2px solid #90caf9' : 'none',
                boxSizing: 'border-box',
              }}
            />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, opacity: 0.7 }}>Brush:</span>
          {[8, 18, 32].map(s => (
            <button
              key={s}
              onClick={() => setBrushSize(s)}
              style={{
                width: s + 12, height: s + 12, borderRadius: '50%', background: selectedColor,
                border: brushSize === s ? '2px solid var(--accent2)' : '2px solid var(--border2)',
                cursor: 'pointer',
              }}
            />
          ))}
        </div>
        {!evaluated && (
          <button className="ab-action-btn" style={{ marginLeft: 'auto', background: 'var(--muted)', fontSize: 13, padding: '4px 14px' }} onClick={handleClear}>
            Clear
          </button>
        )}
      </div>

      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        style={{
          border: '2px solid var(--border2)', borderRadius: 8, cursor: evaluated ? 'default' : 'crosshair',
          width: '100%', maxWidth: W, display: 'block', touchAction: 'none',
          background: '#fff',
        }}
        onMouseDown={startPaint}
        onMouseMove={paint}
        onMouseUp={stopPaint}
        onMouseLeave={stopPaint}
        onTouchStart={startPaint}
        onTouchMove={paint}
        onTouchEnd={stopPaint}
      />

      {zoneResults && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {zoneResults.map(r => (
            <div key={r.zone.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
              <span style={{ width: 14, height: 14, borderRadius: '50%', background: r.zone.color, display: 'inline-block', flexShrink: 0 }} />
              <span style={{ minWidth: 120 }}>{r.zone.label}</span>
              <div style={{ flex: 1, background: 'var(--border)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, Math.round(r.fill * 100))}%`, height: '100%', background: r.pass ? '#43a047' : '#e53935', transition: 'width 0.5s' }} />
              </div>
              <span style={{ minWidth: 42, textAlign: 'right', color: r.pass ? 'var(--green)' : 'var(--red)' }}>
                {Math.round(r.fill * 100)}% {r.pass ? '✓' : '✗'}
              </span>
            </div>
          ))}
        </div>
      )}

      {!evaluated
        ? <button className="ab-action-btn" style={{ marginTop: 14 }} onClick={handleEvaluate}>Evaluate Painting</button>
        : !zoneResults && <div className="ab-submitted-badge">✓ Evaluating...</div>
      }
    </div>
  );
}
